import type {
  HTMLInputAutoCompleteAttribute,
  HTMLInputTypeAttribute,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, ErrorMessage, type FieldInputProps } from 'formik';
import InputErrorMessage from '../shared/InputErrorMessage';
import formStyles from '../../../styles/forms.module.scss';
import type { FormikTouchedErrorsForm } from '../shared/formikTypes';
import { getFieldErrorState, joinClassNames } from '../shared/formFieldUi';
import s from './TextInput.module.scss';

type LabelPosition = 'left' | 'top';

interface TextInputProps {
  form: FormikTouchedErrorsForm;
  field: FieldInputProps<string | number>;
  labelMsg?: string;
  helpMsg?: string;
  labelPosition?: LabelPosition;
  labelClass?: string;
  inline?: boolean;
  fullWidth?: boolean;
  boldLabel?: boolean;
  unit?: string;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  required?: boolean;
  disabled?: boolean;
  fieldType?: HTMLInputTypeAttribute;
}

function TextInput({
  field,
  form,
  labelMsg,
  helpMsg = '',
  labelPosition = 'top',
  labelClass = '',
  inline = false,
  fullWidth = false,
  boldLabel = false,
  unit,
  required = false,
  disabled = false,
  fieldType = 'text',
  autoComplete = 'on',
}: TextInputProps) {
  const { value, name, onChange } = field;
  const { isError } = getFieldErrorState(form, name);

  const wrapperClassName = joinClassNames(
    s.textInput,
    !inline && labelPosition === 'top' && s.labelTop,
    !inline && labelPosition === 'left' && s.labelLeft,
    labelClass,
    fullWidth && s.fullWidth,
    boldLabel && s.bold,
    isError && formStyles.errorBorder
  );

  const labelSpanClassName = joinClassNames(
    s.labelSpan,
    isError && formStyles.errorMessage
  );

  return (
    <label htmlFor={name} className={wrapperClassName}>
      {labelMsg && (
        <span className={labelSpanClassName}>
          <FormattedMessage id={labelMsg} />
          {required && <span>*</span>}
        </span>
      )}
      {helpMsg && (
        <div className={s.helpMsg}>
          <FormattedMessage id={helpMsg} />
        </div>
      )}
      <div className={s.inputUnitWrapper}>
        <Field
          as="input"
          type={fieldType}
          id={name}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={isError}
        />
        {unit && <div className={s.unit}>{unit}</div>}
      </div>
      {labelPosition === 'left' && <div className={s.flexBreak} />}
      <ErrorMessage name={name} component={InputErrorMessage} />
    </label>
  );
}

export default TextInput;
